/**
 * Entwürfe — der geführte Ablauf nach der Quelle.
 *
 * 🔴 Umgebaut am 15.09.2026 nach Matzes Rückmeldung: „Ich weiß nicht, was aktualisiert
 * wird oder was neu angelegt wird. Mir fehlt ein klarer roter Faden." Vorher standen
 * Neuanlage und Aktualisieren als fünf Blöcke untereinander auf einer Seite, mit zwei
 * verschiedenen Tippbestätigungen — ohne dass je gewählt worden wäre, was passieren soll.
 *
 * Jetzt gilt: **Ein Entwurf hat eine Betriebsart, und nur ihr Pfad ist sichtbar.**
 *
 *   Neue Artikel anlegen           Bestand erweitern / aktualisieren
 *   ① Auswahl                      ① Auswahl (die vorhandenen Artikel)
 *   ② Eigenschaften und Tags       ② Felder
 *   ③ Probelauf                    ③ Vorschau
 *   ④ Anlegen                      ④ Schreiben
 *                                  ⑤ Bilder (optional)
 *
 * Jeder Schritt hat eine Nummer, einen Titel und eine Zeile Zustand. Was nicht dran ist,
 * ist zugeklappt oder gesperrt — aber sichtbar, damit klar ist, was noch kommt.
 *
 * 🔴 Es gibt keine Plenty-Sandbox: auch Dev-Instanzen schreiben in den echten Shop
 * (E31). Der Probelauf schreibt nichts; erst Schritt ④ legt Artikel an.
 */
import { useCallback, useEffect, useState } from 'react';
import { PlayCircle, RefreshCw, Trash2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import AktualisierenPanel from '../components/AktualisierenPanel';
import BetriebsartWahl, { betriebsartName, type Betriebsart } from '../components/BetriebsartWahl';
import LaufButton from '../components/LaufButton';
import ProbelaufPlan from '../components/ProbelaufPlan';
import RegelnPanel from '../components/RegelnPanel';
import TeamlinePanel from '../components/TeamlinePanel';
import Schritt from '../components/Schritt';
import SchreiblaufPanel from '../components/SchreiblaufPanel';
import Vorsortierung, { filtere, type GruppenSchluessel } from '../components/Vorsortierung';
import { existenzBefund } from '../lib/existenz';
import { zahl, zeitpunkt } from '../lib/format';
import type {
  EntwurfDetail, EntwurfKurz, FtpOrdner, ProbelaufAntwort, QuellenAntwort, SchreibRegeln,
} from '../lib/typen';

export default function EntwuerfePage() {
  const [entwuerfe, setEntwuerfe] = useState<EntwurfKurz[]>([]);
  const [offen, setOffen] = useState<EntwurfDetail | null>(null);
  const [probelauf, setProbelauf] = useState<ProbelaufAntwort | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Die FTP-Ordner braucht nur „Bilder nachliefern" — ein Fehler dabei darf die
  // Entwurfsliste nicht kippen.
  const [bildOrdner, setBildOrdner] = useState<FtpOrdner[]>([]);
  /** Welche Gruppe die Liste zeigt und worauf die Sammelauswahl wirkt. */
  const [gruppe, setGruppe] = useState<GruppenSchluessel>('alle');
  /** Eine laufende Sammelauswahl — sie kann 241 Artikel betreffen und dauert. */
  const [waehltAus, setWaehltAus] = useState(false);
  const [stelltUm, setStelltUm] = useState(false);
  /** Rückmeldung nach einer Kategorie-Zuweisung — verschwindet beim nächsten Öffnen. */
  const [hinweis, setHinweis] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      const antwort = await api.get<{ data: EntwurfKurz[] }>('/programmes/artikelimport/entwuerfe');
      setEntwuerfe(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Entwürfe nicht ladbar.');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  useEffect(() => {
    void api.get<{ data: QuellenAntwort }>('/programmes/artikelimport/quellen')
      .then(a => setBildOrdner(a.data.data.ftp))
      .catch(() => setBildOrdner([]));
  }, []);

  const oeffnen = useCallback(async (id: number) => {
    setFehler(null);
    setProbelauf(null);
    setHinweis(null);
    try {
      const antwort = await api.get<{ data: EntwurfDetail }>(`/programmes/artikelimport/entwuerfe/${id}`);
      setOffen(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Entwurf nicht ladbar.');
    }
  }, []);

  const auswahlSetzen = useCallback(async (
    id: number, artikel: string[], ausgewaehlt: boolean,
  ) => {
    if (artikel.length === 0) return;
    setWaehltAus(true);
    try {
      await api.patch(`/programmes/artikelimport/entwuerfe/${id}/auswahl`, { artikel, ausgewaehlt });
      await oeffnen(id);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Auswahl nicht speicherbar.');
    } finally {
      setWaehltAus(false);
    }
  }, [oeffnen]);

  /**
   * Betriebsart umstellen — wer sich in der Quelle vertan hat, muss nicht neu einlesen.
   * Das Backend belegt die Auswahl passend neu vor (alles Passende an, alles andere ab).
   */
  const betriebsartUmstellen = useCallback(async (id: number, betriebsart: Betriebsart) => {
    setStelltUm(true);
    setFehler(null);
    setProbelauf(null);
    try {
      await api.patch(`/programmes/artikelimport/entwuerfe/${id}/betriebsart`, { betriebsart });
      setGruppe('alle');
      await oeffnen(id);
      await laden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Umstellen fehlgeschlagen.');
    } finally {
      setStelltUm(false);
    }
  }, [oeffnen, laden]);

  const probelaufStarten = useCallback(async (id: number) => {
    setLaeuft(true);
    setFehler(null);
    setProbelauf(null);
    try {
      const antwort = await api.post<{ data: ProbelaufAntwort }>(
        `/programmes/artikelimport/entwuerfe/${id}/probelauf`, {},
      );
      setProbelauf(antwort.data.data);
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Probelauf fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  }, []);

  const verwerfen = useCallback(async (id: number) => {
    await api.delete(`/programmes/artikelimport/entwuerfe/${id}`);
    if (offen?.entwurf.id === id) setOffen(null);
    await laden();
  }, [laden, offen]);

  // ── Ableitungen für den geöffneten Entwurf ──────────────────────────────
  const betriebsart: Betriebsart = offen?.entwurf.betriebsart === 'felder' ? 'felder' : 'neuanlage';
  const neuanlage = betriebsart === 'neuanlage';
  const sortierbar = (offen?.artikel ?? []).map(a => ({
    ...a,
    schluessel: a.artikelSchluessel,
    // 🔴 NICHT `a.status` — das ist der Zeilenzustand (`bereit`/`uebersprungen`), die
    // Vorsortierung braucht den Existenzbefund. Genau diese Verwechslung liess die
    // Leiste „Anlegen 0 · Unklar 88" anzeigen (22.09.2026).
    existenz: existenzBefund(a),
    ampelKategorie: a.ampelKategorie,
    ampelBild: a.ampelBild,
  }));
  const gefiltert = filtere(sortierbar, gruppe);
  // Was in dieser Betriebsart überhaupt anwählbar ist — die Regel steht über dem Klick.
  const anwaehlbar = (status: string) => (neuanlage ? status !== 'uebersprungen' : status === 'uebersprungen');

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Entwürfe</h1>
          <p className="mt-1 max-w-3xl text-sm text-[#1A1A1A]/70">
            Gespeicherte Import-Vorhaben. Jeder Entwurf hat eine Betriebsart — Neu anlegen
            oder Bestand aktualisieren — und führt Schritt für Schritt durch genau diesen
            einen Weg.
          </p>
        </div>
        <Button variant="outline" onClick={() => void laden()} disabled={laedt}>
          <RefreshCw className={`mr-2 h-4 w-4 ${laedt ? 'animate-spin' : ''}`} />
          {laedt ? 'Wird geladen …' : 'Neu laden'}
        </Button>
      </header>

      {fehler && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {fehler}
        </div>
      )}

      {hinweis && (
        <div className="rounded border border-[#1A1A1A] bg-[#E9FF57]/30 p-3 text-sm text-[#1A1A1A]">
          {hinweis}
        </div>
      )}

      {/* ── Liste ─────────────────────────────────────────────────────────── */}
      <section className="space-y-2">
        {entwuerfe.length === 0 && !laedt && (
          <p className="text-sm text-[#1A1A1A]/60">
            Noch kein Entwurf. Im Reiter „Quelle" eine Datei wählen, Artikel bilden und
            „Als Entwurf speichern".
          </p>
        )}
        {entwuerfe.map(e => (
          <div
            key={e.id}
            className={`flex flex-wrap items-center gap-3 rounded border p-3 text-sm ${
              offen?.entwurf.id === e.id ? 'border-[#1A1A1A] bg-[#E9FF57]/20' : 'border-[#DAD9D9]'
            }`}
          >
            <button
              type="button"
              onClick={() => void oeffnen(e.id)}
              className="min-w-0 flex-1 text-left"
            >
              <span className="font-medium text-[#1A1A1A]">{e.name}</span>
              <span className="ml-2 text-[#1A1A1A]/60">
                {zahl(e.artikelGesamt)} Artikel · {zahl(e.variantenGesamt)} Varianten
                {e.gerechnetAm && ` · ${zeitpunkt(e.gerechnetAm)}`}
              </span>
            </button>
            <span className={`rounded px-2 py-0.5 text-xs ${
              e.betriebsart === 'felder' ? 'bg-amber-50 text-amber-900' : 'bg-green-50 text-green-900'
            }`}>
              {betriebsartName(e.betriebsart)}
            </span>
            {e.benutzerName && (
              <span className="text-xs text-[#1A1A1A]/50">
                {e.eigener ? 'von dir' : e.benutzerName}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => void verwerfen(e.id)} title="Entwurf verwerfen">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </section>

      {/* ── Ein Entwurf: Kopf mit Betriebsart ─────────────────────────────── */}
      {offen && (
        <section className="space-y-3 rounded border-2 border-[#1A1A1A] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#1A1A1A]">{offen.entwurf.name}</h2>
              <p className="text-sm text-[#1A1A1A]/70">
                Quelle: {offen.entwurf.quelle?.dateiname ?? '–'} · {zahl(offen.artikel.length)} Artikel
              </p>
            </div>
            <div className="space-y-1 text-right">
              <p className="text-xs uppercase tracking-wide text-[#1A1A1A]/60">Betriebsart</p>
              <BetriebsartWahl
                kompakt
                wert={betriebsart}
                aufWahl={b => { if (b !== betriebsart && !stelltUm) void betriebsartUmstellen(offen.entwurf.id, b); }}
              />
              {stelltUm && <p className="text-xs text-[#1A1A1A]/60">wird umgestellt …</p>}
            </div>
          </div>
          <p className="text-sm text-[#1A1A1A]/80">
            {neuanlage
              ? <>Dieser Entwurf legt <strong>neue Artikel</strong> in Plenty an. Was schon existiert, wird übersprungen — dafür gibt es die andere Betriebsart.</>
              : <>Dieser Entwurf <strong>aktualisiert bestehende Artikel</strong> in Plenty. Was es dort nicht gibt, bleibt außen vor — dafür gibt es die andere Betriebsart.</>}
          </p>
        </section>
      )}

      {/* ── Schritt 1: Auswahl (beide Pfade) ───────────────────────────────── */}
      {offen && (
        <Schritt
          nummer={1}
          titel={neuanlage ? 'Welche Artikel sollen angelegt werden?' : 'Welche Artikel sollen aktualisiert werden?'}
          zustand={`${zahl(offen.ausgewaehlt)} von ${zahl(offen.artikel.length)} Artikeln ausgewählt`}
          erledigt={offen.ausgewaehlt > 0}
        >
          <div className="space-y-3">
            <Vorsortierung
              artikel={sortierbar}
              aktiv={gruppe}
              aufGruppe={setGruppe}
              beschaeftigt={waehltAus}
              aufAuswahl={(schluessel, ausgewaehlt) => void auswahlSetzen(
                offen.entwurf.id,
                // Die Regel steht über dem Klick: nur, was in dieser Betriebsart Ziel
                // sein kann. Abwählen darf immer.
                ausgewaehlt
                  ? schluessel.filter(sch => anwaehlbar(offen.artikel.find(a => a.artikelSchluessel === sch)?.status ?? ''))
                  : schluessel,
                ausgewaehlt,
              )}
            />

            <div className="max-h-96 divide-y divide-[#DAD9D9] overflow-y-auto border-y border-[#DAD9D9]">
              {gefiltert.map(a => {
                const darf = anwaehlbar(a.status);
                return (
                  <label
                    key={a.artikelSchluessel}
                    className={`flex items-start gap-3 px-2 py-2 text-sm ${
                      darf ? 'cursor-pointer hover:bg-[#F7F7F5]' : 'opacity-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={a.ausgewaehlt}
                      disabled={!darf || waehltAus}
                      onChange={() => void auswahlSetzen(offen.entwurf.id, [a.artikelSchluessel], !a.ausgewaehlt)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-[#1A1A1A]">{a.name}</span>
                      <span className="ml-2 text-[#1A1A1A]/60">{zahl(a.varianten)} Varianten</span>
                      {a.status === 'uebersprungen' && (
                        <span className={`ml-2 rounded px-2 py-0.5 text-xs ${neuanlage ? 'bg-red-50 text-red-900' : 'bg-green-50 text-green-900'}`}>
                          {neuanlage ? 'existiert bereits' : 'in Plenty vorhanden'}
                        </span>
                      )}
                      {a.status !== 'uebersprungen' && !neuanlage && (
                        <span className="ml-2 rounded bg-[#F7F7F5] px-2 py-0.5 text-xs text-[#1A1A1A]/60">
                          nicht im Bestand
                        </span>
                      )}
                      {a.ampelBild === 'rot' && (
                        <span className="ml-2 rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-900" title="Wird trotzdem angelegt — Bilder lassen sich danach nachliefern.">
                          ohne Bild
                        </span>
                      )}
                      {a.ampelBild === 'gelb' && (
                        <span className="ml-2 rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-900">Bilder unvollständig</span>
                      )}
                      {a.hinweise.length > 0 && (
                        <span className="mt-0.5 block text-xs text-[#1A1A1A]/60">{a.hinweise.join(' · ')}</span>
                      )}
                    </span>
                  </label>
                );
              })}
              {gefiltert.length === 0 && (
                <p className="px-2 py-4 text-sm text-[#1A1A1A]/60">
                  Kein Artikel in dieser Gruppe. Die Auswahl der anderen Artikel bleibt
                  unberührt — „Alle" zeigt sie wieder.
                </p>
              )}
            </div>
          </div>
        </Schritt>
      )}

      {/* ══ Pfad NEUANLAGE ══════════════════════════════════════════════════ */}
      {offen && neuanlage && (
        <>
          <Schritt
            nummer={2}
            titel="Eigenschaften und Tags"
            zustand={`${zahl(offen.entwurf.regeln.eigenschaften.length)} Eigenschaften · ${zahl(offen.entwurf.regeln.tags.length)} Tags — vorbelegt: GPSR und Tag 203`}
            erledigt
            offen={false}
          >
            <RegelnPanel
              entwurfId={offen.entwurf.id}
              regeln={offen.entwurf.regeln}
              kategoriewerte={offen.entwurf.kategoriewerte}
              aufRegelnGeaendert={(regeln: SchreibRegeln) => setOffen(
                v => (v ? { ...v, entwurf: { ...v.entwurf, regeln } } : v),
              )}
            />
          </Schritt>

          <TeamlinePanel entwurfId={offen.entwurf.id} schrittNummer={3} />

          <Schritt
            nummer={4}
            titel="Probelauf — was würde passieren?"
            zustand={probelauf
              ? `${zahl(probelauf.plan.summen.anlegen)} würden angelegt · ${zahl(probelauf.plan.summen.existiert)} existieren · ${zahl(probelauf.plan.summen.nichtAnlegbar)} nicht anlegbar · ${zahl(probelauf.aufwand.gesamt)} Schreibvorgänge`
              : 'Rechnet jeden ausgewählten Artikel durch. Schreibt nichts nach Plenty.'}
            erledigt={probelauf != null}
            gesperrt={offen.ausgewaehlt === 0}
            gesperrtGrund="Erst mindestens einen Artikel auswählen."
          >
            <div className="space-y-4">
              <LaufButton
                laeuft={laeuft}
                laufText="Probelauf läuft"
                dauerHinweis="rechnet jeden Artikel durch, schreibt nichts"
                icon={<PlayCircle className="mr-2 h-4 w-4" />}
                onClick={() => void probelaufStarten(offen.entwurf.id)}
                className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
              >
                {probelauf ? 'Probelauf erneut' : 'Probelauf starten'}
              </LaufButton>
              {probelauf && (
                <ProbelaufPlan
                  ergebnis={probelauf}
                  entwurfId={offen.entwurf.id}
                  aufKategorieGesetzt={meldung => {
                    // Nach der Zuweisung beides neu holen: die Liste zeigt die Ampel,
                    // der Probelauf die Kategoriezahl. Sonst steht die alte Aussage da.
                    setHinweis(meldung);
                    void oeffnen(offen.entwurf.id).then(() => probelaufStarten(offen.entwurf.id));
                  }}
                />
              )}
            </div>
          </Schritt>

          <Schritt
            nummer={5}
            titel="In PlentyMarkets anlegen"
            ton="gefahr"
            zustand={probelauf
              ? `${zahl(probelauf.plan.summen.anlegen)} Artikel würden angelegt. Tippbestätigung nötig.`
              : 'Erst den Probelauf ansehen.'}
            gesperrt={probelauf == null}
            gesperrtGrund="Erst den Probelauf ansehen — er zeigt, was dieser Schritt tun würde."
          >
            <SchreiblaufPanel
              entwurfId={offen.entwurf.id}
              ausgewaehlt={offen.ausgewaehlt}
              bildOrdner={bildOrdner}
              artikel={offen.artikel}
              berichtEmpfaenger={offen.entwurf.berichtEmpfaenger}
            />
          </Schritt>
        </>
      )}

      {/* ══ Pfad AKTUALISIEREN ══════════════════════════════════════════════ */}
      {offen && !neuanlage && (
        <AktualisierenPanel entwurfId={offen.entwurf.id} bildOrdner={bildOrdner} schrittAb={2} />
      )}
    </div>
  );
}
